/**
 * Locale-aware formatting utilities.
 * Delegates to the formatting registry (DIP) - supports injection for testing.
 */

import { getFormattingRegistry } from 'utils/formatting';
import { resolveIsoDateValue } from 'utils/formatting/dateInputUtils';
import { getFormattingDateLocale } from 'utils/formatting/formattingLocale';

/**
 * Formats a number as currency using the current app locale.
 */
export function formatCurrency(amount: number, currencyCode?: string): string {
  return getFormattingRegistry().currency.format(amount, currencyCode);
}

/**
 * Formats a number for display using the current app locale.
 */
export function formatNumber(value: number | string, options?: Intl.NumberFormatOptions): string {
  return getFormattingRegistry().number.format(value, options);
}

/**
 * Formats a date using the current app locale.
 */
export function formatDate(date: Date | string, formatKey = 'DATE_ALPHA_SHORT'): string {
  return getFormattingRegistry().date.formatDate(date, formatKey);
}

/**
 * Formats a display date for an explicitly supplied locale.
 *
 * Use this when a component must render from its reactive locale state rather
 * than the globally synchronized formatting locale.
 */
export function formatDateForLocale(date: Date, locale: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
}

/**
 * Formats a date using the same locale-specific display pipeline as date inputs.
 * This preserves the configured numeric date pattern for read-only date values.
 */
export function formatDateInput(date: string): string {
  const registry = getFormattingRegistry();
  const locale = getFormattingDateLocale() ?? registry.locale.getLocale();
  const isoDate = resolveIsoDateValue(
    date,
    (displayValue) => registry.dateInput?.parseToIso(displayValue, locale) ?? displayValue,
    'entrata',
  );

  if (!isoDate || !registry.dateInput) return date;
  return registry.dateInput.formatForDisplay(isoDate, locale);
}

/**
 * Formats a date with long month name.
 */
export function formatDateLong(date: Date | string): string {
  return getFormattingRegistry().date.formatDateLong(date);
}

/**
 * Formats date and time using the current app locale.
 */
export function formatDateTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  return getFormattingRegistry().date.formatDateTime(date, options);
}
